import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, X, IdCard, FileText, Wallet, User2, Trash2, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const dl = (dataUrl, filename) => {
  const bin = atob(dataUrl.split(',')[1]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const openPdf = (dataUrl) => {
  const bin = atob(dataUrl.split(',')[1]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  window.open(URL.createObjectURL(blob), '_blank');
};

const CNIC_MASK = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 13);
  if (d.length <= 5) return d;
  if (d.length <= 12) return `${d.slice(0, 5)}-${d.slice(5)}`;
  return `${d.slice(0, 5)}-${d.slice(5, 12)}-${d.slice(12)}`;
};

export default function HRPayrollManagement() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('hr');
  const [hr, setHr] = useState({});
  const [structure, setStructure] = useState({ basic_salary: 0, allowances: [], deductions: [] });
  const [advance, setAdvance] = useState({ amount: '', repayment_months: '', reason: '' });
  const [advances, setAdvances] = useState([]);
  const [payslipMonth, setPayslipMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payslipDaysMode, setPayslipDaysMode] = useState('full');       // full | days
  const [payslipDaysWorked, setPayslipDaysWorked] = useState('');
  const [payslipDaysBasis, setPayslipDaysBasis] = useState('actual');   // actual | 30

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(data || []);
    } catch (e) { toast.error('Failed to load users'); }
  };

  const loadDetails = async (u) => {
    setSelected(u);
    setHr({
      cnic: u.cnic || '',
      bank_account_number: u.bank_account_number || '',
      date_of_joining: u.date_of_joining ? u.date_of_joining.slice(0, 10) : '',
      date_of_birth: u.date_of_birth ? u.date_of_birth.slice(0, 10) : '',
      department: u.department || '', designation: u.designation || '',
      employee_id: u.employee_id || '',
      emergency_contact: u.emergency_contact || '', blood_group: u.blood_group || '',
    });
    try {
      const s = await axios.get(`${API_URL}/api/payroll/structure/${u.id}`, { withCredentials: true });
      const sd = s.data || {};
      setStructure({
        basic_salary: sd.basic_salary || 0,
        allowances: sd.allowances || [],
        deductions: sd.deductions || [],
      });
      const a = await axios.get(`${API_URL}/api/payroll/advances?user_id=${u.id}`, { withCredentials: true });
      setAdvances(a.data || []);
    } catch (e) {}
  };

  useEffect(() => { loadUsers(); }, []);

  const saveHR = async () => {
    try {
      await axios.put(`${API_URL}/api/payroll/employee/${selected.id}/hr-fields`, {
        ...hr,
        date_of_joining: hr.date_of_joining ? `${hr.date_of_joining}T00:00:00Z` : null,
        date_of_birth: hr.date_of_birth ? `${hr.date_of_birth}T00:00:00Z` : null,
      }, { withCredentials: true });
      toast.success('HR details saved'); loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const saveStructure = async () => {
    try {
      const payload = {
        user_id: selected.id,
        basic_salary: parseFloat(structure.basic_salary || 0),
        allowances: structure.allowances.filter(a => a.name).map(a => ({ ...a, amount: parseFloat(a.amount || 0) })),
        deductions: structure.deductions.filter(d => d.name).map(d => ({ ...d, amount: parseFloat(d.amount || 0) })),
      };
      const { data } = await axios.post(`${API_URL}/api/payroll/structure/v2`, payload, { withCredentials: true });
      toast.success(`Structure saved — Net PKR ${data.net_salary.toLocaleString()}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const submitAdvance = async () => {
    try {
      const amt = parseFloat(advance.amount || 0);
      const mo = parseInt(advance.repayment_months || 0, 10);
      if (!(amt > 0) || !(mo > 0)) { toast.error('Amount and months required'); return; }
      const { data } = await axios.post(`${API_URL}/api/payroll/advances`, {
        user_id: selected.id, amount: amt, repayment_months: mo, reason: advance.reason,
      }, { withCredentials: true });
      toast.success(`Advance recorded — PKR ${data.monthly_deduction.toLocaleString()}/mo`);
      setAdvance({ amount: '', repayment_months: '', reason: '' });
      loadDetails(selected);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const issuePayslip = async () => {
    try {
      let url = `${API_URL}/api/payroll/payslip/v2/${selected.id}?month=${payslipMonth}`;
      if (payslipDaysMode === 'days' && payslipDaysWorked) {
        url += `&days_worked=${encodeURIComponent(payslipDaysWorked)}&days_basis=${encodeURIComponent(payslipDaysBasis)}`;
      }
      const { data } = await axios.post(url, {}, { withCredentials: true });
      toast.success(`Payslip ${data.slip_number} — Net PKR ${data.net_salary.toLocaleString()}${data.is_days_wise ? ` (${data.days_worked}/${data.days_in_month} days)` : ''}`);
      dl(data.pdf, `payslip_${data.slip_number}.pdf`);
      loadDetails(selected);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const issueIdCard = async (mode = 'download') => {
    try {
      const { data } = await axios.post(`${API_URL}/api/payroll/id-card/${selected.id}`, {}, { withCredentials: true });
      if (mode === 'view') openPdf(data.pdf);
      else dl(data.pdf, `id_card_${data.card_number}.pdf`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addAllowance = () => setStructure((s) => ({ ...s, allowances: [...s.allowances, { name: '', amount: 0, frequency: 'monthly' }] }));
  const addDeduction = () => setStructure((s) => ({ ...s, deductions: [...s.deductions, { name: '', amount: 0, frequency: 'monthly', description: '' }] }));
  const removeAllowance = (i) => setStructure((s) => ({ ...s, allowances: s.allowances.filter((_, idx) => idx !== i) }));
  const removeDeduction = (i) => setStructure((s) => ({ ...s, deductions: s.deductions.filter((_, idx) => idx !== i) }));

  const total_a = (structure.allowances || []).reduce((n, a) => n + (parseFloat(a.amount) || 0), 0);
  const total_d = (structure.deductions || []).reduce((n, d) => n + (parseFloat(d.amount) || 0), 0);
  const gross = (parseFloat(structure.basic_salary) || 0) + total_a;
  const net = gross - total_d;

  return (
    <div className="space-y-6" data-testid="hr-payroll-page">
      <div>
        <h1 className="text-3xl font-bold text-white mb-1">HR & Payroll</h1>
        <p className="text-slate-400 text-sm">Employee records, salary structure, advances, payslips and ID cards.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-slate-900 border-slate-800 lg:col-span-1">
          <CardHeader><CardTitle className="text-white text-base">Employees ({users.length})</CardTitle></CardHeader>
          <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
            <Table>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}
                    className={`border-slate-800 cursor-pointer ${selected?.id === u.id ? 'bg-slate-800/60' : 'hover:bg-slate-800/40'}`}
                    onClick={() => loadDetails(u)} data-testid={`emp-row-${u.id}`}>
                    <TableCell>
                      <div className="text-white text-sm font-mono">{u.employee_id || u.username}</div>
                      <div className="text-slate-400 text-xs">{u.role}</div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-white text-base flex items-center">
              <User2 className="h-4 w-4 mr-2 text-amber-500" />
              {selected ? `${selected.employee_id || selected.username} — ${selected.role}` : 'Select an employee'}
            </CardTitle>
            {selected && (
              <div className="flex gap-2">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => issueIdCard('view')} data-testid="btn-idcard-view">
                  <IdCard className="h-3 w-3 mr-1" /> ID Card
                </Button>
              </div>
            )}
          </CardHeader>
          {selected && (
            <CardContent>
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-slate-800">
                  <TabsTrigger value="hr" data-testid="tab-hr">HR Details</TabsTrigger>
                  <TabsTrigger value="structure" data-testid="tab-structure">Salary Structure</TabsTrigger>
                  <TabsTrigger value="advances" data-testid="tab-advances">Advances</TabsTrigger>
                  <TabsTrigger value="payslip" data-testid="tab-payslip">Payslip</TabsTrigger>
                </TabsList>

                {/* --- HR Details --- */}
                <TabsContent value="hr" className="space-y-3 mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-400 text-xs">CNIC (45203-4566456-1)</Label>
                      <Input value={hr.cnic} onChange={(e) => setHr({ ...hr, cnic: CNIC_MASK(e.target.value) })}
                        className="bg-slate-800 border-slate-700 text-white font-mono" data-testid="hr-cnic" /></div>
                    <div><Label className="text-slate-400 text-xs">Employee Code</Label>
                      <Input value={hr.employee_id} onChange={(e) => setHr({ ...hr, employee_id: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" data-testid="hr-empcode" /></div>
                    <div><Label className="text-slate-400 text-xs">Bank Account Number</Label>
                      <Input value={hr.bank_account_number} onChange={(e) => setHr({ ...hr, bank_account_number: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" data-testid="hr-bank" /></div>
                    <div><Label className="text-slate-400 text-xs">Department</Label>
                      <Input value={hr.department} onChange={(e) => setHr({ ...hr, department: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" data-testid="hr-dept" /></div>
                    <div><Label className="text-slate-400 text-xs">Designation / Post</Label>
                      <Input value={hr.designation} onChange={(e) => setHr({ ...hr, designation: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" data-testid="hr-desig" /></div>
                    <div><Label className="text-slate-400 text-xs">Date of Joining</Label>
                      <Input type="date" value={hr.date_of_joining} onChange={(e) => setHr({ ...hr, date_of_joining: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div><Label className="text-slate-400 text-xs">Date of Birth</Label>
                      <Input type="date" value={hr.date_of_birth} onChange={(e) => setHr({ ...hr, date_of_birth: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div><Label className="text-slate-400 text-xs">Emergency Contact</Label>
                      <Input value={hr.emergency_contact} onChange={(e) => setHr({ ...hr, emergency_contact: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div><Label className="text-slate-400 text-xs">Blood Group</Label>
                      <Input value={hr.blood_group} onChange={(e) => setHr({ ...hr, blood_group: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" placeholder="e.g. B+" /></div>
                  </div>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveHR} data-testid="btn-save-hr">Save HR Details</Button>
                </TabsContent>

                {/* --- Salary Structure --- */}
                <TabsContent value="structure" className="space-y-3 mt-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Basic Salary (PKR)</Label>
                    <Input type="number" value={structure.basic_salary}
                      onChange={(e) => setStructure({ ...structure, basic_salary: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white" data-testid="struct-basic" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-slate-300 text-sm">Allowances</Label>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addAllowance} data-testid="add-allowance">
                        <Plus className="h-3 w-3 mr-1" /> Add
                      </Button>
                    </div>
                    {(structure.allowances || []).map((a, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                        <Input placeholder="Allowance name" value={a.name}
                          onChange={(e) => { const c = [...structure.allowances]; c[i].name = e.target.value; setStructure({ ...structure, allowances: c }); }}
                          className="col-span-5 bg-slate-800 border-slate-700 text-white text-xs" />
                        <Input placeholder="Amount" type="number" value={a.amount}
                          onChange={(e) => { const c = [...structure.allowances]; c[i].amount = e.target.value; setStructure({ ...structure, allowances: c }); }}
                          className="col-span-4 bg-slate-800 border-slate-700 text-white text-xs" />
                        <Input placeholder="monthly" value={a.frequency || 'monthly'}
                          onChange={(e) => { const c = [...structure.allowances]; c[i].frequency = e.target.value; setStructure({ ...structure, allowances: c }); }}
                          className="col-span-2 bg-slate-800 border-slate-700 text-white text-xs" />
                        <Button size="sm" variant="ghost" onClick={() => removeAllowance(i)}
                          className="col-span-1 text-red-400 hover:text-red-500 h-8"><X className="h-3 w-3" /></Button>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-slate-300 text-sm">Deductions</Label>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addDeduction} data-testid="add-deduction">
                        <Plus className="h-3 w-3 mr-1" /> Add
                      </Button>
                    </div>
                    {(structure.deductions || []).map((d, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                        <Input placeholder="Deduction name" value={d.name}
                          onChange={(e) => { const c = [...structure.deductions]; c[i].name = e.target.value; setStructure({ ...structure, deductions: c }); }}
                          className="col-span-4 bg-slate-800 border-slate-700 text-white text-xs" />
                        <Input placeholder="Amount" type="number" value={d.amount}
                          onChange={(e) => { const c = [...structure.deductions]; c[i].amount = e.target.value; setStructure({ ...structure, deductions: c }); }}
                          className="col-span-3 bg-slate-800 border-slate-700 text-white text-xs" />
                        <Input placeholder="Description" value={d.description || ''}
                          onChange={(e) => { const c = [...structure.deductions]; c[i].description = e.target.value; setStructure({ ...structure, deductions: c }); }}
                          className="col-span-4 bg-slate-800 border-slate-700 text-white text-xs" />
                        <Button size="sm" variant="ghost" onClick={() => removeDeduction(i)}
                          className="col-span-1 text-red-400 hover:text-red-500 h-8"><X className="h-3 w-3" /></Button>
                      </div>
                    ))}
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded-sm p-3 grid grid-cols-4 text-sm">
                    <div><div className="text-slate-400 text-xs">Basic</div><div className="text-white">PKR {(parseFloat(structure.basic_salary) || 0).toLocaleString()}</div></div>
                    <div><div className="text-slate-400 text-xs">+ Allowances</div><div className="text-emerald-400">PKR {total_a.toLocaleString()}</div></div>
                    <div><div className="text-slate-400 text-xs">− Deductions</div><div className="text-red-400">PKR {total_d.toLocaleString()}</div></div>
                    <div><div className="text-slate-400 text-xs">Net Salary</div><div className="text-amber-400 font-bold" data-testid="struct-net">PKR {net.toLocaleString()}</div></div>
                  </div>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveStructure} data-testid="btn-save-structure">Save Structure</Button>
                </TabsContent>

                {/* --- Advances --- */}
                <TabsContent value="advances" className="space-y-3 mt-4">
                  <div className="grid grid-cols-4 gap-2 items-end">
                    <div><Label className="text-slate-400 text-xs">Amount (PKR)</Label>
                      <Input type="number" value={advance.amount} onChange={(e) => setAdvance({ ...advance, amount: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" data-testid="adv-amount" /></div>
                    <div><Label className="text-slate-400 text-xs">Repayment Months</Label>
                      <Input type="number" value={advance.repayment_months} onChange={(e) => setAdvance({ ...advance, repayment_months: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" data-testid="adv-months" /></div>
                    <div><Label className="text-slate-400 text-xs">Reason</Label>
                      <Input value={advance.reason} onChange={(e) => setAdvance({ ...advance, reason: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" /></div>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950" onClick={submitAdvance} data-testid="btn-add-advance">Record Advance</Button>
                  </div>
                  <Table>
                    <TableHeader><TableRow className="border-slate-800">
                      <TableHead className="text-slate-400">Amount</TableHead>
                      <TableHead className="text-slate-400">Monthly</TableHead>
                      <TableHead className="text-slate-400">Recovered</TableHead>
                      <TableHead className="text-slate-400">Remaining</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Reason</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {advances.length === 0 && <TableRow><TableCell colSpan={6} className="text-slate-500 text-center">No advances</TableCell></TableRow>}
                      {advances.map((a) => (
                        <TableRow key={a.id} className="border-slate-800" data-testid={`adv-row-${a.id}`}>
                          <TableCell className="text-white">PKR {a.amount.toLocaleString()}</TableCell>
                          <TableCell className="text-amber-400">PKR {a.monthly_deduction.toLocaleString()}</TableCell>
                          <TableCell className="text-emerald-400">PKR {a.recovered_amount.toLocaleString()}</TableCell>
                          <TableCell className="text-white">PKR {a.remaining_balance.toLocaleString()}</TableCell>
                          <TableCell><span className={`text-xs px-2 py-0.5 rounded-sm ${a.status === 'active' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{a.status}</span></TableCell>
                          <TableCell className="text-slate-400 text-xs">{a.reason || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TabsContent>

                {/* --- Payslip --- */}
                <TabsContent value="payslip" className="space-y-3 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                    <div><Label className="text-slate-400 text-xs">Month</Label>
                      <Input type="month" value={payslipMonth} onChange={(e) => setPayslipMonth(e.target.value)}
                        className="bg-slate-800 border-slate-700 text-white" data-testid="payslip-month" /></div>
                    <div>
                      <Label className="text-slate-400 text-xs">Mode</Label>
                      <select value={payslipDaysMode} onChange={(e) => setPayslipDaysMode(e.target.value)}
                        data-testid="payslip-mode"
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-white text-sm">
                        <option value="full">Full Month</option>
                        <option value="days">Days-Wise</option>
                      </select>
                    </div>
                    {payslipDaysMode === 'days' && (
                      <>
                        <div>
                          <Label className="text-slate-400 text-xs">Days Worked</Label>
                          <Input type="number" min="0" max="31" value={payslipDaysWorked}
                            onChange={(e) => setPayslipDaysWorked(e.target.value)}
                            data-testid="payslip-days-worked"
                            className="bg-slate-800 border-slate-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">Daily Basis</Label>
                          <select value={payslipDaysBasis} onChange={(e) => setPayslipDaysBasis(e.target.value)}
                            data-testid="payslip-days-basis"
                            className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-white text-sm">
                            <option value="actual">Actual days in month (28/30/31)</option>
                            <option value="30">Fixed ÷ 30</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={issuePayslip} data-testid="btn-issue-payslip">
                    <FileDown className="h-3 w-3 mr-1" /> Issue Payslip (View / Print / PDF)
                  </Button>
                  <p className="text-slate-500 text-xs">Any active salary advance is automatically deducted this month until fully recovered. Auto monthly salary credit runs on the 1st of every month via the platform cron.</p>
                </TabsContent>
              </Tabs>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
